import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import RoleSelector from './RoleSelector';
import { fetchWithAuth } from '../../auth/fetchWithAuth';
import { roleLabel } from '../../auth/permissions';
import { API_URL } from '../../../config/env';

export default function EditUserModal({ show, onClose, actor, user, onUpdated }) {
  const [role, setRole] = useState('engineer');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !user) return;
    setRole(user.role || 'engineer');
    setError(null);
  }, [show, user]);

  const handleSubmit = async () => {
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: { role } }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload.error || payload.errors?.join(', ') || 'Failed to update role');
      }
      onUpdated(payload);
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div className="d-flex justify-content-between w-100">
      <button type="button" className="btn btn-link text-muted" onClick={onClose} disabled={submitting}>
        <i className="bi bi-arrow-left me-1" /> Cancel
      </button>
      <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting || !user || role === user.role}>
        Save
      </button>
    </div>
  );

  return (
    <AdminModal show={show} onClose={onClose} title="Edit User Role" footer={footer} size="sm">
      {!user ? null : (
        <>
          <div className="mb-2">
            <div className="small text-muted">User</div>
            <div className="fw-semibold">{user.email}</div>
          </div>
          <div className="mb-3">
            <label htmlFor="invite_role" className="form-label">Role</label>
            <RoleSelector actor={actor} value={role} onChange={setRole} disabled={submitting} />
            <div className="form-text">
              Current role: {roleLabel(user.role)}
            </div>
          </div>
          {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}
        </>
      )}
    </AdminModal>
  );
}

EditUserModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  actor: PropTypes.object,
  user: PropTypes.object,
  onUpdated: PropTypes.func,
};

EditUserModal.defaultProps = {
  actor: null,
  user: null,
  onUpdated: () => {},
};
